import { useState } from "react";
import clsx from "clsx";

import {
  DataCell,
  DataRow,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@breakingthelines/design-system";

import type { ArchetypeVersion } from "@/lib/admin-api";

import {
  comparableVersions,
  diffVersions,
  formatWeight,
  previousVersion,
  type WeightChange,
} from "./archetype-diff";
import { formatArchetypeDate, statusLabel } from "./archetype-form";
import styles from "./archetype-editor.module.css";

function author(name?: string, id?: string): string {
  return name || id || "";
}

/**
 * Every version of one archetype, newest first, and what each changed
 * against the one before it. Read only: an older version comes back by
 * editing the draft, never by restoring a row.
 */
export function ArchetypeHistory({ versions }: { versions: readonly ArchetypeVersion[] }) {
  if (versions.length === 0) return null;
  return (
    <section className={styles.history}>
      <h2 className={styles.sectionTitle}>History</h2>
      <DataTable
        label="History"
        columns="minmax(70px, 0.4fr) minmax(90px, 0.6fr) minmax(110px, 0.8fr) minmax(140px, 1fr) minmax(110px, 0.8fr) minmax(140px, 1fr)"
        header={["Version", "Status", "Created", "Created by", "Published", "Published by"]}
      >
        {versions.map((version) => {
          const publishedBy = author(version.publishedByName, version.publishedById);
          return (
            <DataRow key={version.version} className={styles.historyRow}>
              <DataCell className={styles.figure}>v{version.version}</DataCell>
              <DataCell label="Status">{statusLabel(version.status)}</DataCell>
              <DataCell label="Created" className={styles.mutedCell}>
                {formatArchetypeDate(version.createdAt)}
              </DataCell>
              <DataCell label="Created by" className={styles.mutedCell}>
                {author(version.createdByName, version.createdById)}
              </DataCell>
              {/* An unpublished version has no mobile caption for these two,
                so its card does not show empty labels. */}
              <DataCell
                label={version.publishedAt ? "Published" : undefined}
                className={styles.mutedCell}
              >
                {formatArchetypeDate(version.publishedAt)}
              </DataCell>
              <DataCell
                label={publishedBy ? "Published by" : undefined}
                className={styles.mutedCell}
              >
                {publishedBy}
              </DataCell>
            </DataRow>
          );
        })}
      </DataTable>
      <VersionChanges versions={versions} />
    </section>
  );
}

const CHANGE_WORDS: Record<WeightChange, string> = {
  added: "Added",
  removed: "Removed",
  changed: "Changed",
  same: "",
};

/**
 * What one version changed against the version before it: the fields that
 * differ, then every weight in either version. The newest pair shows first;
 * a dropdown picks an older one.
 */
function VersionChanges({ versions }: { versions: readonly ArchetypeVersion[] }) {
  const comparable = comparableVersions(versions);
  const [picked, setPicked] = useState<number | null>(null);
  const after = comparable.find((v) => v.version === picked) ?? comparable[0];
  const before = after ? previousVersion(versions, after.version) : null;
  if (!after || !before) return null;
  const diff = diffVersions(before, after);
  const pairLabel = (v: ArchetypeVersion) =>
    `v${v.version} against v${previousVersion(versions, v.version)?.version ?? ""}`;

  return (
    <div className={styles.changes}>
      <div className={styles.changesHeader}>
        <h3 className={styles.changesTitle}>Changes</h3>
        <Select
          value={String(after.version)}
          onValueChange={(value: string | null) => {
            if (value) setPicked(Number(value));
          }}
        >
          <SelectTrigger aria-label="Changes in" className={styles.changesTrigger}>
            <SelectValue>
              {(value: string | null) => {
                const v = comparable.find((c) => String(c.version) === value);
                return v ? pairLabel(v) : "";
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {comparable.map((v) => (
              <SelectItem key={v.version} value={String(v.version)}>
                {pairLabel(v)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {diff.unchanged ? <p className={styles.muted}>Same definition as v{before.version}</p> : null}

      <DataTable
        label="Changes"
        columns="minmax(140px, 1.4fr) minmax(70px, 0.6fr) minmax(70px, 0.6fr) minmax(80px, 0.6fr)"
        header={["", `v${before.version}`, `v${after.version}`, ""]}
      >
        {diff.fields.map((field) => (
          <DataRow key={field.label} className={styles.historyRow}>
            <DataCell>{field.label}</DataCell>
            <DataCell label={`v${before.version}`} className={styles.mutedCell}>
              {field.before}
            </DataCell>
            <DataCell label={`v${after.version}`}>{field.after}</DataCell>
            <DataCell className={styles.mutedCell}>Changed</DataCell>
          </DataRow>
        ))}
        {diff.notesChanged ? (
          <DataRow className={styles.historyRow}>
            <DataCell>Notes</DataCell>
            <DataCell />
            <DataCell />
            <DataCell className={styles.mutedCell}>Changed</DataCell>
          </DataRow>
        ) : null}
        {diff.weights.map((row) => (
          <DataRow
            key={row.metricKey}
            className={clsx(styles.historyRow, row.change === "same" && styles.unchangedRow)}
          >
            <DataCell>{row.label}</DataCell>
            <DataCell
              label={row.before == null ? undefined : `v${before.version}`}
              className={clsx(styles.figure, styles.mutedCell)}
            >
              {formatWeight(row.before)}
            </DataCell>
            <DataCell
              label={row.after == null ? undefined : `v${after.version}`}
              className={styles.figure}
            >
              {formatWeight(row.after)}
            </DataCell>
            <DataCell className={styles.mutedCell}>{CHANGE_WORDS[row.change]}</DataCell>
          </DataRow>
        ))}
      </DataTable>
    </div>
  );
}
